import api from "./api"

/**
 * Config do PDVoz por estabelecimento (endpoints em sistemaadm: PdvozResource).
 * Auth = o mesmo Basic que o login guarda em localStorage "chaveSistema" — a PWA
 * usa o MESMO endpoint pra LER a config depois do login, ao abrir o app e a cada 30 min.
 * O que vai no JSON e como o PDVoz o lê: ver pages/PDVoz/parametros.ts.
 */
const authHeader = () => localStorage.getItem("chaveSistema") || ""

/** Mensagem legível de um erro do axios (o backend manda { mensagem } no corpo). */
export function mensagemDeErro(e: any): string {
    const d = e?.response?.data
    if (d && typeof d === "object") return d.mensagem || d.erro || d.message || e?.message || "erro desconhecido"
    if (typeof d === "string" && d.trim()) return d.slice(0, 300)
    return e?.message || String(e)
}

export async function getPdvozConfig(
    idEstabelecimento: number | string
): Promise<Record<string, any>> {
    const { data } = await api.get(`pdvoz/config/${idEstabelecimento}`, {
        headers: { Authorization: authHeader() },
    })
    // o backend devolve o envelope padrão (dados: null) quando nunca foi salvo
    return data && typeof data === "object" ? data : {}
}

export async function salvarPdvozConfig(
    config: Record<string, any>,
    idEstabelecimento: number | string
): Promise<void> {
    await api.post(`pdvoz/config/${idEstabelecimento}`, config, {
        headers: {
            "Content-Type": "application/json",
            Authorization: authHeader(),
        },
    })
}

// ─── Palavras difíceis (banco da loja, dentro do JSON do PDVoz) ──────────────
// Endpoint próprio: o POST da config não mexe no banco (o backend preserva o gravado),
// pra o painel salvando parâmetros não apagar o que um aparelho acabou de treinar.

export type OperacaoPalavras =
    | { operacao: "mesclar" | "substituir"; palavras: any[] }
    | { operacao: "remover"; chaves: string[] }

export async function alterarPalavrasPdvoz(
    idEstabelecimento: number | string,
    corpo: OperacaoPalavras
): Promise<any[]> {
    const { data } = await api.post(`pdvoz/palavras/${idEstabelecimento}`, corpo, {
        headers: { "Content-Type": "application/json", Authorization: authHeader() },
    })
    if (!data || !Array.isArray(data.palavrasDificeis)) throw new Error(data?.mensagem || "Resposta inesperada do servidor.")
    return data.palavrasDificeis
}

// ─── Segredos da loja (ParametroGeral, fora do JSON do PDVoz) ─────────────────
// Stone Code (STONE_CODE), PIN do gerente (PDVOZ_PIN_GERENTE) e senha de estorno
// (PDVOZ_SENHA_ESTORNO). O backend os INJETA no GET /pdvoz/config e os DESCARTA no POST
// — por isso têm endpoints próprios (IntegracaoParceirosResource), que validam os dígitos
// e forçam publico = 0.

export interface SegredosPdvoz {
    stoneCode: string
    pinGerente: string
    senhaEstorno: string
}

export async function obterSegredosPdvoz(): Promise<SegredosPdvoz> {
    const headers = { Authorization: authHeader() }
    const [stone, senhas] = await Promise.all([
        api.get("integracaoParceiros/ObterStone", { headers }),
        api.get("integracaoParceiros/ObterSenhasPdvoz", { headers }),
    ])
    return {
        stoneCode: stone.data?.stoneCode || "",
        pinGerente: senhas.data?.pinGerente || "",
        senhaEstorno: senhas.data?.senhaEstorno || "",
    }
}

/** "" LIMPA o Stone Code da loja (o backend exige a chave no corpo). */
export async function salvarStoneCode(stoneCode: string): Promise<void> {
    await api.post("integracaoParceiros/SalvarStone", { stoneCode }, {
        headers: { "Content-Type": "application/json", Authorization: authHeader() },
    })
}

/** Só as chaves presentes são tocadas; "" LIMPA aquele segredo. */
export async function salvarSenhasPdvoz(
    senhas: Partial<Pick<SegredosPdvoz, "pinGerente" | "senhaEstorno">>
): Promise<void> {
    await api.post("integracaoParceiros/SalvarSenhasPdvoz", senhas, {
        headers: { "Content-Type": "application/json", Authorization: authHeader() },
    })
}

// ─── Categorias do cardápio (pra escolher as que a cozinha anuncia) ──────────
// O MESMO endpoint que o PDVoz usa (GET /rest/integracao/cardapio/{id}: lista plana
// com `categoria` em cada item), pra o nome casar com o que a cozinha compara.

export async function listarCategoriasCardapio(idEstabelecimento: number | string): Promise<string[]> {
    const { data } = await api.get(`integracao/cardapio/${idEstabelecimento}`, {
        headers: { Authorization: authHeader() },
    })
    const itens: any[] = Array.isArray(data) ? data : []
    const nomes = itens.map(i => String(i?.categoria || "").trim()).filter(Boolean)
    return Array.from(new Set(nomes)).sort((a, b) => a.localeCompare(b, "pt-BR"))
}

// ─── Templates do WhatsApp oficial (conta da Cliente Fiel) ───────────────────

export type QualTemplate = "pix" | "pixCartao" | "comprovante"

/** { data: [{ name, status }] } — status APPROVED / PENDING / REJECTED… */
export async function statusTemplatesPdvoz(wabaId: string): Promise<{ name: string; status: string }[]> {
    const { data } = await api.post("whatsapp/status-templates-pdvoz", { wabaId }, {
        headers: { "Content-Type": "application/json", Authorization: authHeader() },
    })
    if (data?.erro) throw new Error(data.erro)
    return Array.isArray(data?.data) ? data.data : []
}

/** Pede à Meta pra criar SÓ um template (o que falta). */
export async function solicitarTemplatePdvoz(p: {
    wabaId: string
    qual: QualTemplate
    nomePix: string
    nomePixCartao: string
    nomeComprovante: string
}): Promise<any> {
    const { data } = await api.post("whatsapp/criar-template-pix", p, {
        headers: { "Content-Type": "application/json", Authorization: authHeader() },
    })
    if (data?.erro) throw new Error(data.erro)
    return data
}

/** Dispara um template pra um número de teste, com dados de exemplo. */
export async function testarTemplatePdvoz(p: {
    phoneId: string
    templateName: string
    telefone: string
    qual: QualTemplate
    idEstabelecimento: number | string
}): Promise<any> {
    // mesma regra do PDVoz (comCodigoPais): DDD + número ganha o 55; o resto vai como veio
    const digitos = p.telefone.replace(/\D/g, "")
    const telefone = digitos.length === 10 || digitos.length === 11 ? `55${digitos}` : digitos
    if (telefone.length < 12) throw new Error("Telefone inválido (informe DDD + número).")
    const urlButtonValues: Record<string, string> =
        p.qual === "pix" ? { "0": "c=teste123&v=1550" }
            : p.qual === "pixCartao" ? { "0": "c=teste123&v=1550", "1": encodeURIComponent("https://exemplo.com/pagar") }
                : {}
    const { data } = await api.post(
        "whatsapp/enviar-template-botao",
        {
            phoneId: p.phoneId,
            templateName: p.templateName,
            telefone,
            bodyParams: ["Loja Teste", "42", "R$ 15,50", "1x Item de teste R$ 15,50"],
            urlButtonValues,
        },
        {
            headers: {
                "Content-Type": "application/json",
                Authorization: authHeader(),
                estabelecimento: String(p.idEstabelecimento),
            },
        }
    )
    if (data?.erro) throw new Error(data.erro)
    return data
}

// ─── Maquininhas da loja (modo da tela, POR APARELHO) ─────────────────────────
// Cadastro das maquininhas Stone (DispositivoRecebimento, CobrancaTerminalResource). Cada
// uma se registra no login e ao abrir o app; aqui se escolhe em que tela ELA abre. Não vai no
// JSON do PDVoz: aquele vale pra loja inteira, e o modo é de cada aparelho.

export type ModoTela = "" | "garcom" | "recebimento"

export interface Maquininha {
    id: string
    nome?: string
    online?: boolean
    ultimoVistoEm?: number | string
    /** Ausente = nunca definido (o aparelho ainda não se registrou depois da mudança). */
    modoTela?: ModoTela
}

export async function listarMaquininhas(idEstabelecimento: number | string): Promise<Maquininha[]> {
    const { data } = await api.get("cobranca-terminal/dispositivos", {
        params: { idEstabelecimento },
        headers: { Authorization: authHeader() },
    })
    return Array.isArray(data) ? data : []
}

export async function definirModoMaquininha(
    idEstabelecimento: number | string,
    id: string,
    modoTela: ModoTela
): Promise<Maquininha> {
    const { data } = await api.post("cobranca-terminal/dispositivo/modo", null, {
        params: { idEstabelecimento, id, modoTela },
        headers: { Authorization: authHeader() },
    })
    return data
}

/**
 * Batiza uma maquininha.
 *
 * ⚠ Deixou de ser opcional no PDVoz 1.94: a tela de Configurações do aparelho virou só leitura,
 * então um terminal novo se registra SEM NOME e este é o único lugar onde ele ganha um. O app lê
 * o nome de volta no próximo registro (login, abertura e a cada 30 min).
 */
export async function definirNomeDaMaquininha(
    idEstabelecimento: number,
    id: string,
    nome: string
): Promise<Maquininha> {
    const { data } = await api.post("cobranca-terminal/dispositivo/nome", null, {
        params: { idEstabelecimento, id, nome },
        headers: { Authorization: authHeader() },
    })
    return data
}
